/**
 * Status indicator utilities for AG-UI Chat.
 *
 * Provides animated status indicators for thinking, processing, and tool calls.
 */

export type IndicatorType = 'connecting' | 'thinking' | 'processing' | 'tool-call';

export interface IndicatorConfig {
  type: IndicatorType;
  icon?: string;
  text: string;
}

/**
 * Default icons for each indicator type.
 */
const INDICATOR_ICONS: Record<IndicatorType, string | undefined> = {
  'connecting': undefined,
  'thinking': undefined,
  'processing': '⚙️',
  'tool-call': '🔧',
};

/**
 * Creates an animated dots element for status indicators.
 */
function createAnimatedDots(): string {
  return `
    <span class="agui-chat__thinking-dots">
      <span>.</span><span>.</span><span>.</span>
    </span>
  `;
}

/**
 * Creates a status indicator element.
 */
export function createStatusIndicator(config: IndicatorConfig): HTMLElement {
  const indicator = document.createElement('div');
  indicator.classList.add('agui-chat__message', `agui-chat__message--${config.type}`);

  const icon = config.icon ?? INDICATOR_ICONS[config.type];

  const iconHtml = icon ? `<span class="agui-chat__${config.type}-icon">${icon}</span>` : '';
  const textClass = config.type === 'thinking' ? 'thinking-text' : `${config.type.replace('-', '-')}-text`;

  indicator.innerHTML = `
    ${iconHtml}
    <span class="agui-chat__${textClass}">${config.text}</span>
    ${createAnimatedDots()}
  `;

  return indicator;
}

/**
 * Manages a collection of status indicators.
 */
export class StatusIndicatorManager {
  private container: HTMLElement;
  private connectingIndicator: HTMLElement | null = null;
  private thinkingIndicator: HTMLElement | null = null;
  private processingIndicator: HTMLElement | null = null;
  private toolCallIndicators: Map<string, HTMLElement> = new Map();
  private scrollCallback: () => void;

  constructor(container: HTMLElement, scrollCallback: () => void) {
    this.container = container;
    this.scrollCallback = scrollCallback;
  }

  /**
   * Repoints the manager at a different messages container.
   *
   * New indicators append to the given container; indicators already shown
   * elsewhere (e.g. before an AguiChat.retarget()) stay where they are but
   * remain dismissible, since the per-id node references held here are
   * unaffected.
   */
  setContainer(container: HTMLElement): void {
    this.container = container;
  }

  /**
   * Shows the connecting indicator - the earliest feedback in the request
   * lifecycle, shown before the run has even reached the server. Covers the
   * gap a delayed or buffering proxy would otherwise leave blank, since it
   * doesn't wait on any response event the way showThinking() does.
   */
  showConnecting(): void {
    if (this.connectingIndicator) return;

    this.connectingIndicator = createStatusIndicator({
      type: 'connecting',
      text: 'Connecting',
    });
    this.container.appendChild(this.connectingIndicator);
    this.scrollCallback();
  }

  /**
   * Hides the connecting indicator.
   */
  hideConnecting(): void {
    if (this.connectingIndicator) {
      this.connectingIndicator.remove();
      this.connectingIndicator = null;
    }
  }

  /**
   * Shows the thinking indicator.
   */
  showThinking(): void {
    if (this.thinkingIndicator) return;

    this.thinkingIndicator = createStatusIndicator({
      type: 'thinking',
      text: 'Thinking',
    });
    this.container.appendChild(this.thinkingIndicator);
    this.scrollCallback();
  }

  /**
   * Hides the thinking indicator.
   */
  hideThinking(): void {
    if (this.thinkingIndicator) {
      this.thinkingIndicator.remove();
      this.thinkingIndicator = null;
    }
  }

  /**
   * Shows the processing indicator.
   */
  showProcessing(): void {
    if (this.processingIndicator) return;

    this.processingIndicator = createStatusIndicator({
      type: 'processing',
      text: 'Processing results',
    });
    this.container.appendChild(this.processingIndicator);
    this.scrollCallback();
  }

  /**
   * Hides the processing indicator.
   */
  hideProcessing(): void {
    if (this.processingIndicator) {
      this.processingIndicator.remove();
      this.processingIndicator = null;
    }
  }

  /**
   * Shows a tool call indicator.
   */
  showToolCall(toolCallId: string, toolName: string): void {
    // Hide other indicators first
    this.hideThinking();
    this.hideProcessing();

    const indicator = createStatusIndicator({
      type: 'tool-call',
      text: `Calling <strong>${toolName}</strong> to help`,
    });

    this.toolCallIndicators.set(toolCallId, indicator);
    this.container.appendChild(indicator);
    this.scrollCallback();
  }

  /**
   * Hides a specific tool call indicator.
   */
  hideToolCall(toolCallId: string): void {
    const indicator = this.toolCallIndicators.get(toolCallId);
    if (indicator) {
      indicator.remove();
      this.toolCallIndicators.delete(toolCallId);
    }
  }

  /**
   * Returns the number of active tool call indicators.
   */
  get activeToolCallCount(): number {
    return this.toolCallIndicators.size;
  }

  /**
   * Clears all indicators.
   */
  clearAll(): void {
    this.hideConnecting();
    this.hideThinking();
    this.hideProcessing();
    this.toolCallIndicators.forEach((indicator) => indicator.remove());
    this.toolCallIndicators.clear();
  }
}

